/**
 * The swell the spot page shows, shared with `GET /api/forecast.json`.
 *
 * Primary swell (`swell_wave_*`) wins when Open-Meteo has it. Significant
 * wave height / period / direction is the fallback, one component at a
 * time, which is what the dashboard readout did when current swell was
 * null. A period under 5 s is wind chop: it cannot be the reported swell
 * when a longer-period component carries more energy (height squared).
 * Flagler on 2026-11-02 was that case — a 3 s average against a 7 s sea.
 */

export const MIN_PRIMARY_SWELL_PERIOD_S = 5;

export interface DisplayedSwellInput {
  swellHeightM: number | null;
  swellPeriodS: number | null;
  swellDirectionDeg: number | null;
  /** Significant wave height. The dashboard calls this offshore height. */
  waveHeightM: number | null;
  wavePeriodS: number | null;
  waveDirectionDeg: number | null;
}

export interface DisplayedSwell {
  heightM: number | null;
  periodS: number | null;
  directionDeg: number | null;
}

function finite(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function orNull(value: number | null | undefined): number | null {
  return finite(value) ? value : null;
}

/** Wave energy scales with the square of height. */
function energy(heightM: number): number {
  return heightM * heightM;
}

function isChopAgainstLongerEnergy(input: DisplayedSwellInput): boolean {
  const { swellHeightM, swellPeriodS, waveHeightM, wavePeriodS } = input;
  if (!finite(swellHeightM) || !finite(swellPeriodS) || !finite(waveHeightM))
    return false;
  if (!finite(wavePeriodS)) return false;
  if (swellPeriodS >= MIN_PRIMARY_SWELL_PERIOD_S) return false;
  if (wavePeriodS <= swellPeriodS) return false;
  return energy(waveHeightM) > energy(swellHeightM);
}

export function selectDisplayedSwell(input: DisplayedSwellInput): DisplayedSwell {
  if (isChopAgainstLongerEnergy(input)) {
    return {
      heightM: orNull(input.waveHeightM),
      periodS: orNull(input.wavePeriodS),
      directionDeg: orNull(input.waveDirectionDeg),
    };
  }
  return {
    heightM: finite(input.swellHeightM) ? input.swellHeightM : orNull(input.waveHeightM),
    periodS: finite(input.swellPeriodS) ? input.swellPeriodS : orNull(input.wavePeriodS),
    directionDeg: finite(input.swellDirectionDeg)
      ? input.swellDirectionDeg
      : orNull(input.waveDirectionDeg),
  };
}
